import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Eye, EyeOff, LogOut } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, logout, isAdmin, user } = useAuth();
  const navigate = useNavigate();

  // NOTE: we intentionally DO NOT auto-redirect admins to /dashboard here.
  // The previous behaviour ("if isAdmin → Navigate to /dashboard") let
  // visitors slip into the vault on a stale client-side session — they'd
  // see the dashboard render, then the very next API call would 401 with
  // "sessione scaduta". Forcing the password prompt on every fresh visit
  // to /vault-9821 keeps the admin entrance honest.

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Trim whitespace/invisible chars introduced by browser autofill on
    // mobile keyboards or password managers (root cause of "16 dots" bug).
    const cleanEmail = email.trim();
    const cleanPassword = password.trim();
    try {
      const data = await login(cleanEmail, cleanPassword);
      if (data.role !== "admin") {
        toast.error("Accesso amministrativo richiesto.");
        setSubmitting(false);
        return;
      }
      toast.success("Benvenuto nel Vault.");
      navigate("/vault-9821/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non valide.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      toast.success("Sessione chiusa.");
    } catch {
      toast.error("Errore durante la disconnessione.");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "var(--bg-primary)" }}
      data-testid="vault-login-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-12">
          <span className="overline text-[10px] mb-3 block">— Sezione riservata —</span>
          <h1 className="text-3xl sm:text-4xl font-light">Vault · 9821</h1>
          <p className="text-sm mt-3" style={{ color: "var(--text-secondary)" }}>
            Inserisci le credenziali amministrative per accedere al pannello di controllo.
          </p>
        </div>

        {isAdmin && (
          <div
            className="mb-8 p-4 flex items-center justify-between gap-4"
            style={{
              border: "1px solid var(--border-red)",
              background: "rgba(255,40,0,0.04)",
            }}
            data-testid="vault-existing-session"
          >
            <div className="text-sm">
              <span
                className="overline text-[10px] block mb-1"
                style={{ color: "var(--accent-red)" }}
              >
                ● Sessione esistente
              </span>
              <span style={{ color: "var(--text-secondary)" }}>
                {user?.email || "Admin"}
              </span>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase px-3 py-2 transition-colors"
              style={{
                color: "var(--text-primary)",
                border: "1px solid var(--border-subtle)",
              }}
              data-testid="vault-signout-btn"
            >
              <LogOut size={12} strokeWidth={1.6} />
              Esci
            </button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-8" autoComplete="off">
          {/* Honeypot inputs to neutralize aggressive browser autofill */}
          <input type="text" name="fake-username" autoComplete="username" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
          <input type="password" name="fake-password" autoComplete="new-password" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />

          <div>
            <label className="overline mb-3 block">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="gd-input"
              placeholder="admin@garagedrops.com"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              name="vault-email"
              data-testid="vault-email-input"
            />
          </div>
          <div>
            <label className="overline mb-3 block">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="gd-input pr-12"
                placeholder="••••••••"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                name="vault-password"
                data-testid="vault-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-opacity hover:opacity-100"
                style={{ color: "var(--text-secondary)", opacity: 0.7 }}
                aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                tabIndex={-1}
                data-testid="vault-password-toggle"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {password.length > 0 && (
              <p
                className="text-[10px] mt-2 tracking-wider uppercase"
                style={{ color: "var(--text-secondary)", opacity: 0.6 }}
                data-testid="vault-password-length"
              >
                {password.length} caratteri
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="gd-button gd-button-primary w-full"
            data-testid="vault-submit-btn"
          >
            {submitting ? "Verifica…" : "Apri il Vault"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
